<script setup lang="ts">
import { computed, toRef, useSlots } from 'vue';

import type {
  KaVeeFormFieldEmitContract,
  KaVeeFormFieldPropsContract,
} from '@/kasten-components/components/KaVeeFormField';
import { useDataTestid } from '@/kasten-components/composables';
import type { HTMLElementEvent } from '@/kasten-components/types';

import { KaDescription } from '../KaDescription';
import { KaLabel } from '../KaLabel';
import { KaMessage } from '../KaMessage';

export type KaRangeSliderProps = KaVeeFormFieldPropsContract<number> & {
  id?: string;
  modelValue?: number;
  name: string;
  label: string;
  hideLabel?: boolean;
  description?: string;
  hideDescription?: boolean;
  status?: 'error';
  errorMessage?: string;
  /** Used to link an error message element, handled outside of this component, to this component accessibly. This should not be used if the error message is provided via the `errorMessage` prop.
   * This is useful when you want to share the same error message for multiple inputs, such as in a fieldset.
   * The error message element should have an `id` that matches this value.
   * This will also be used to set the `aria-errormessage` attribute on the input element.
   * If not provided, a fallback ID will be generated based on the input's ID.
   * @default undefined
   */
  errorMessageId?: string;
  /** Whether to visually hide the error message so that it is still present in the DOM and only visible to screen-readers. */
  hideErrorMessage?: boolean;
  /** Remove the error message from the DOM completely. This will keep aria invalid properties and error styling, but allow you to put your own error message in the DOM.
   * This is most likely used when you use this input in a fieldset with other inputs that share the same error message and you want to see the error styling but tie the error message to the fieldset instead of the input.
   * @default false
   */
  showErrorStylesOnly?: boolean;
  min?: number;
  max?: number;
  step?: number;
  hideValue?: boolean;
  /** Renders a visual required asterisk on the label. */
  required?: boolean;
  /** Renders a visual required asterisk on the label. */
  ariaRequired?: boolean;
  /** Only use this property if you plan to add the asterisk in the slot content of the label.
   * A required field MUST have a visual indicator to be accessible.
   * https://www.w3.org/WAI/WCAG22/Techniques/aria/ARIA2
   * https://veeam.hub.essentia11y.com/manual-evaluations/67af37db16faa72414aa1705/results/AMP256/view?skipScanStandards=true&evaluationStandard=WCAG_v22_AAA&status=toReview,open,partiallyFixed,notFixed,cannotBeFixed,validationRequested&issueId=AMP256&linkedPropertyData=6762fe26f5d3a5eaed29f2c8%7C6723d15450ee91f82f056d41
   */
  hideRequiredAsterisk?: boolean;
};

defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<KaRangeSliderProps>(), {
  modelValue: 0,
  min: 0,
  max: 100,
  step: 1,
  hideValue: false,
});

// prettier-ignore
const emit = defineEmits< KaVeeFormFieldEmitContract<HTMLElementEvent<HTMLInputElement>> & { (e: 'update:modelValue', value: number): void; (e: 'input', event: HTMLElementEvent<HTMLInputElement>): void; (e: 'change', event: HTMLElementEvent<HTMLInputElement>): void; (e: 'blur', event: FocusEvent): void; } >();

const progressStyle = computed(() => {
  const { modelValue, min, max } = props;
  const clampedValue = Math.max(min, Math.min(modelValue, max));
  const relativeValue = (clampedValue - min) / (max - min);
  return {
    width: `${relativeValue * 100}%`,
  };
});

const slots = useSlots();

const name = toRef(props, 'name');
const id = computed(() => props.id ?? name.value);
const { dataTestid, suffix } = useDataTestid(id);
// I purposely did not set `aria-required` on the input because it is not a valid ARIA attribute for a range input.
// BEST PRACTICE: If a value must be changed from the default, indicate that via instruction or by setting an initial value that is clearly not the desired final value, rather than using aria-required.
const isRequired = computed(() => props.required || props.ariaRequired);
const labelId = computed(() => `${id.value}-label`);
const fallbackErrorMessageId = computed(() => `${id.value}-error-message`);
const descriptionId = computed(() =>
  props.description || slots.description ? `${id.value}-description` : undefined,
);

const shouldDisplayDescription = computed(
  () => (props.description || slots.description) && !props.hideDescription,
);

const rangeVal = computed({
  get() {
    return props.modelValue;
  },
  set(val) {
    emit('update:modelValue', val);
  },
});

const hasError = computed(() => props.errorMessage !== undefined);
const computedErrorMessageId = computed(() => {
  if (props.showErrorStylesOnly) return props.errorMessageId || undefined;
  return hasError.value ? fallbackErrorMessageId.value : undefined;
});
</script>
<template>
  <div>
    <KaLabel
      :id="labelId"
      :data-testid="suffix('label')"
      :for="id"
      :hide-label="props.hideLabel"
      :class="!shouldDisplayDescription ? 'mx-0 mb-3 mt-0' : 'm-0'"
      :required="isRequired"
      :hide-required-asterisk="props.hideRequiredAsterisk"
    >
      <slot name="label" v-bind="{ label: props.label }">
        {{ props.label }}
        <span v-if="!props.hideValue" class="ml-auto" aria-hidden="true">{{ rangeVal }}</span>
      </slot>
    </KaLabel>

    <KaDescription
      v-if="props.description || $slots.description"
      :id="descriptionId"
      :data-testid="suffix('description')"
      :description="props.description"
      :hide-description="props.hideDescription"
      :class="shouldDisplayDescription ? 'mx-0 mb-3 mt-0' : 'm-0'"
    >
      <slot name="description" v-bind="{ description: props.description }">{{
        props.description
      }}</slot>
    </KaDescription>

    <div class="relative flex h-5 w-full items-center">
      <input
        v-bind="$attrs"
        :id="id"
        v-model.number="rangeVal"
        :data-testid="dataTestid"
        :name="name"
        type="range"
        :min="props.min"
        :max="props.max"
        :step="props.step"
        class="h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 text-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:bg-navy-600 dark:text-blue-400 dark:focus-visible:ring-blue-400 dark:focus-visible:ring-offset-navy-900 [&::-moz-range-thumb]:relative [&::-moz-range-thumb]:z-[1] [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-current [&::-webkit-slider-thumb]:relative [&::-webkit-slider-thumb]:z-[1] [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:ring-2 [&::-webkit-slider-thumb]:ring-current [&::-webkit-slider-thumb]:dark:bg-navy-900"
        :aria-describedby="descriptionId"
        :aria-invalid="props.showErrorStylesOnly || hasError"
        :aria-errormessage="computedErrorMessageId"
        @change="emit('change', $event as HTMLElementEvent<HTMLInputElement>)"
        @input="emit('input', $event as unknown as HTMLElementEvent<HTMLInputElement>)"
        @blur="emit('blur', $event)"
      />
      <span
        class="pointer-events-none absolute h-2 rounded-s-lg bg-blue-500 peer-disabled:bg-opacity-50 dark:bg-blue-400"
        :style="progressStyle"
      ></span>
    </div>

    <KaMessage
      v-if="!props.showErrorStylesOnly && hasError"
      :id="fallbackErrorMessageId"
      :data-testid="suffix('error-message')"
      :message="props.errorMessage"
      background="transparent"
      variant="error"
      :role="null"
      :class="['mt-2', { 'sr-only': props.hideErrorMessage }]"
    />
  </div>
</template>
